import React, { useEffect, useMemo, useState } from "react";
import { Form, Select, Spin, Input } from "antd";
import TextArea from "antd/es/input/TextArea";
import { useTranslation } from "react-i18next";
import { formatToSQLDate, convertToDayjs } from "../../../../utils/dateUtils";
import { useExamSessions } from "../hooks/usePeadgogyExamSessions";
import { useSubjects } from "../../../../components/Academic/subject/hooks/useSubjects";
import {
  CreateExamSessionPayload,
  UpdateExamSessionPayload,
} from "../../../../types/markTypes";
import { ExamType } from "types/examTypeTypes";
import { Subject } from "types/subjectTypes";
import { ClassData } from "types/classTypes";
import CustomModal from "../../../../components/Common/CustomModal";
import CommonMultiSelect from "../../../../components/Common/CommonMultiSelect";
import LocalizedDatePicker from "components/Common/ui/LocalizedDatePicker";
import {
  getBranchLabelFromCode,
  getSubjectLabelFromCode,
} from "utils/schoolUtils";

const { Option } = Select;

interface ExamSessionModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (
    values:
      | CreateExamSessionPayload
      | (UpdateExamSessionPayload & { id: string })
  ) => Promise<boolean>;
  isSubmitting: boolean;
  initialValues?: Partial<CreateExamSessionPayload & { id?: string }>;
  isEdit?: boolean;
}

const ExamSessionModal: React.FC<ExamSessionModalProps> = ({
  visible,
  onClose,
  onSubmit,
  isSubmitting,
  initialValues,
  isEdit = false,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const [subjectId, setSubjectId] = useState<string>();
  const [classLevelId, setClassLevelId] = useState<string>();
  const [subjectOptions, setSubjectOptions] = useState<Subject[]>([]);
  const [classesFiltered, setClassesFiltered] = useState<ClassData[]>([]);
  const [branches, setBranches] = useState<
    { id: string; name: string; labelKey: string }[]
  >([]);
  const [isLoadingOnChange, setIsLoadingOnChange] = useState(false);

  const {
    classes,
    fetchClassesBySubject,
    classLevels,
    handleGetClassLevels,
    examTypes,
    getExamTypes,
  } = useExamSessions();

  const { subjectWithExamType, fetchSubjectsWithExamType } = useSubjects();

  const examTypeId = Form.useWatch("examTypeId", form);
  const semester = Form.useWatch("semester", form);
  const selectedClassIds: string[] = Form.useWatch("classIds", form) ?? [];

  const isPersonalExam =
    Form.useWatch("isPersonalExam", form) ??
    form.getFieldValue("isPersonalExam");

  const branchId =
    Form.useWatch("branchId", form) ?? form.getFieldValue("branchId");

  useEffect(() => {
    handleGetClassLevels();
    fetchSubjectsWithExamType();
  }, []);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setIsLoadingOnChange(true);
      if (subjectId) await fetchClassesBySubject(subjectId);

      if (subjectId && classLevelId && !isPersonalExam) {
        await getExamTypes({ subjectId, classLevelId, branchId });
      }

      if (mounted) setIsLoadingOnChange(false);
    };

    load();
    return () => {
      mounted = false;
    };
  }, [subjectId, classLevelId, isPersonalExam, branchId]);

  useEffect(() => {
    if (initialValues && subjectWithExamType.length && classLevels.length) {
      const levelId = initialValues.examType?.classLevelId ?? "";
      const subjId = initialValues.examType?.subject?.id ?? "";

      form.setFieldsValue({
        ...initialValues,
        startDate: initialValues.startDate
          ? convertToDayjs(initialValues.startDate)
          : undefined,
        classLevelId: levelId,
        subjectId: subjId,
        examTypeId: initialValues.examTypeId,
        classIds: initialValues.classIds || [],
        isPersonalExam: initialValues.examType?.optional ?? false,
        branchId: initialValues.branchId,
        semester: initialValues.semester, // ✅ keep semester in edit
      });

      setClassLevelId(levelId);
      setSubjectId(subjId);

      const _subjectOptions = subjectWithExamType.filter((s) =>
        s.examType?.some((et: any) => et.classLevelId === levelId)
      );
      setSubjectOptions(_subjectOptions);

      getExamTypes({ subjectId: subjId, classLevelId: levelId, branchId });

      setClassesFiltered(classes.filter((cls) => cls.classLevelId === levelId));
      setBranches(classLevels.find((cls) => cls.id === levelId)?.branch ?? []);
    } else {
      form.resetFields();
      form.setFieldsValue({ isPersonalExam: false });
    }
  }, [initialValues, subjectWithExamType, classLevels]);

  const alreadyHasThisExamType = (
    cls: any,
    _examTypeId?: string,
    _semester?: string
  ) => {
    if (!_examTypeId || !_semester) return false;

    return (cls.classExamSession ?? []).some((ces: any) => {
      return (
        ces?.examSession?.examTypeId === _examTypeId &&
        ces?.examSession?.semester === _semester
      );
    });
  };


  const availableClasses = useMemo(() => {
    if (!semester) return [];

    if (isPersonalExam) return classesFiltered;

    return classesFiltered.filter((cls: any) => {
      if (isEdit && selectedClassIds.includes(cls.id)) return true;
      return !alreadyHasThisExamType(cls, examTypeId, semester);
    });
  }, [
    classesFiltered,
    isPersonalExam,
    examTypeId,
    semester,
    isEdit,
    selectedClassIds,
  ]);

  useEffect(() => {
    if (!isEdit) {
      form.setFieldsValue({ classIds: [] });
      return;
    }
    if (
      initialValues?.semester &&
      semester &&
      initialValues.semester !== semester
    ) {
      form.setFieldsValue({ classIds: [] });
    }
    if (
      initialValues?.examTypeId &&
      examTypeId &&
      initialValues.examTypeId !== examTypeId
    ) {
      form.setFieldsValue({ classIds: [] });
    }
  }, [examTypeId, semester]);

  const handleFinish = async (values: Record<string, any>) => {
    const payload = {
      name: values.name,
      note: values.note,
      startDate: values.startDate ? formatToSQLDate(values.startDate) : "",
      semester: values.semester,
      examTypeId: values.examTypeId ?? null,
      classIds: values.classIds,
      isPersonalExam: values.isPersonalExam,
      subjectId: values.subjectId,
      classLevelId: values.classLevelId,
      branchId: values.branchId,
    };

    const finalPayload =
      isEdit && initialValues?.id ? { id: initialValues.id, ...payload } : payload;

    const result = await onSubmit(finalPayload as any);
    if (result) {
      form.resetFields();
      onClose();
    }
  };

  const onChangeClassLevel = (value: string) => {
    setClassLevelId(value);
    setSubjectId(undefined);
    setClassesFiltered([]);

    form.setFieldsValue({
      subjectId: undefined,
      examTypeId: undefined,
      semester: undefined, // ✅ reset semester
      classIds: [],
      branchId: undefined,
    });

    const selectedBranches =
      classLevels.find((cls) => cls.id === value)?.branch ?? [];
    setBranches(selectedBranches);

    const filteredSubjects = subjectWithExamType.filter((s) =>
      s.examType?.some((et: any) => et.classLevelId === value)
    );
    setSubjectOptions(filteredSubjects);
  };

  const onChangeSubject = (value: string) => {
    setSubjectId(value);

    form.setFieldsValue({
      examTypeId: undefined,
      semester: undefined, 
      classIds: [],
    });

    setClassesFiltered(
      classes.filter((cls) => cls.classLevelId === classLevelId)
    );
  };

  const onChangeBranch = (value: string) => {
    form.setFieldsValue({
      branchId: value,
      examTypeId: undefined,
      semester: undefined, 
      classIds: [],
      subjectId: undefined,
    });
  };

  useEffect(() => {
    if (classLevelId && classes.length) {
      setClassesFiltered(classes.filter((cls) => cls.classLevelId === classLevelId));
    }
  }, [classes, classLevelId]);

  useEffect(() => {
    if (branchId && classes.length && branches.length) {
      setClassesFiltered(classes.filter((cls: any) => cls.branchId === branchId));
    }
  }, [classes, branchId, branches]);

  return (
    <CustomModal
      title={isEdit ? t("edit_exam_session") : t("create_exam_session")}
      isOpen={visible}
      onClose={onClose}
      onSave={async () => {
        try {
          await form.validateFields();
          form.submit();
        } catch (_) { }
      }}
      saveText={isEdit ? t("update") : t("create")}
      loading={isSubmitting}
    >
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item
          name="name"
          label={t("session_name")}
          rules={[{ required: true, message: t("enter_session_name") }]}
        >
          <Input placeholder={t("enter_session_name")} />
        </Form.Item>

        <Form.Item
          name="classLevelId"
          label={t("class_level")}
          rules={[{ required: true, message: t("validation.select_class_level") }]}
        >
          <Select placeholder={t("class_level")} onChange={onChangeClassLevel}>
            {classLevels.map((level) => (
              <Option key={level.id} value={level.id}>
                {level.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {branches.length > 0 && (
          <Form.Item
            name="branchId"
            label={t("branch")}
            rules={[{ required: true, message: t("validation.select_branch") }]}
          >
            <Select placeholder={t("branch")} onChange={onChangeBranch}>
              {branches.map((branch) => (
                <Option key={branch.id} value={branch.id}>
                  {getBranchLabelFromCode(branch?.labelKey, t)}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        <Form.Item
          name="subjectId"
          label={t("subject")}
          rules={[{ required: true, message: t("validation.select_subject") }]}
        >
          <Select
            placeholder={t("subject")}
            onChange={onChangeSubject}
            disabled={!classLevelId || (branches.length > 0 && !branchId)}
          >
            {subjectOptions.map((subject) => (
              <Option key={subject.id} value={subject.id}>
                {getSubjectLabelFromCode(subject?.code ?? "", t)}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {!isEdit && (
          <Form.Item
            name="isPersonalExam"
            label={t("exam_type")}
            rules={[{ required: true, message: t("validation.select_exam_type") }]}
          >
            <Select placeholder={t("exam_type")}>
              <Option value={true}>{t("personal")}</Option>
              <Option value={false}>{t("official")}</Option>
            </Select>
          </Form.Item>
        )}

        {!isPersonalExam &&
          (isLoadingOnChange ? (
            <Spin size="large" />
          ) : (
            <Form.Item
              name="examTypeId"
              label={t("examinations")}
              rules={[{ required: true, message: t("validation.select_exam_type") }]}
            >
              <Select placeholder={t("examinations")} disabled={!subjectId}>
                {examTypes?.map((examType: ExamType) => (
                  <Option key={examType.id} value={examType.id}>
                    {examType.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          ))}

        <Form.Item
          name="semester"
          label={t("semester")}
          rules={[{ required: true, message: t("select_semester") }]}
        >
          <Select
            placeholder={t("select_semester")}
            disabled={!isPersonalExam && !examTypeId}
          >
            <Option value="S1">{t("semester_1")}</Option>
            <Option value="S2">{t("semester_2")}</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="classIds"
          label={t("classes")}
          rules={[{ required: true, message: t("select_classes") }]}
        >
          {isLoadingOnChange ? (
            <Spin size="large" />
          ) : (
            <CommonMultiSelect
              disabled={!semester || (!examTypeId && !isPersonalExam)}
              value={form.getFieldValue("classIds")}
              onChange={(selected) => form.setFieldsValue({ classIds: selected })}
              placeholder={!semester ? t("select_semester") : t("select_classes")}
              options={availableClasses.map((cls) => ({
                value: cls.id,
                label: cls.name,
              }))}
            />
          )}
        </Form.Item>

        <Form.Item name="note" label={t("note")}>
          <TextArea placeholder={t("note")} rows={2} />
        </Form.Item>

        <Form.Item
          name="startDate"
          label={t("start_date")}
          rules={[{ required: true, message: t("enter_start_date") }]}
        >
          <LocalizedDatePicker className="w-100" placeholder={t("start_date")} />
        </Form.Item>
      </Form>
    </CustomModal>
  );
};

export default ExamSessionModal;
